/**
 * PHARMAKON — the team login and the owner's workstation administration (X XWP-72 consumer of the
 * XWP-70 / XWP-71 server, as built 2026-10-24: `routes/equipe-auth.routes.js`,
 * `routes/compte-equipe.routes.js`).
 *
 * Three kinds of call, never mixed (the API client attaches what each needs):
 *  · public — the team login: `connexion`, `verification` (the poste and duration step posts WITH the
 *    owner's code), `renouveler` (the workstation's refresh token);
 *  · the workstation's team token (`jetonEquipe` in the request config — it wins over a user's token):
 *    the picker, the photos, the PIN switch, the workstation's own logout;
 *  · the picked user's own JWT (the normal bearer): `prolonger`, `changer`, `preuve`;
 *  · the PROPRIÉTAIRE (`compte_equipe:gerer`): the team account and « Sessions actives ».
 */
import api from "./api.client.js";

const avec = (jeton, extra = {}) => ({ jetonEquipe: jeton, ...extra });

export const equipeApi = {
  /** POST /auth/equipe/connexion — step 1: the code goes to every active owner (mail sandbox outside production). */
  connexion: (identifiant, mot_de_passe) => api.post("/auth/equipe/connexion", { identifiant, mot_de_passe }),
  /** POST /auth/equipe/verification — the code, the poste label, the saved duration and the idle delay. */
  verification: (corps) => api.post("/auth/equipe/verification", corps),
  /** POST /auth/equipe/renouveler — one rotation (409 within the grace: another window renewed). */
  renouveler: (refresh_token) => api.post("/auth/equipe/renouveler", { refresh_token }),
  /** GET /auth/equipe/moi — the workstation's session (team token). */
  moi: (jeton) => api.get("/auth/equipe/moi", avec(jeton)),
  /** POST /auth/equipe/deconnexion — « Fermer le poste » (team token). */
  deconnexion: (jeton) => api.post("/auth/equipe/deconnexion", undefined, avec(jeton)),
  /** GET /auth/equipe/utilisateurs — the picker: name, role, photo state, PIN state (team token). */
  utilisateurs: (jeton) => api.get("/auth/equipe/utilisateurs", avec(jeton)),
  /** GET /auth/equipe/utilisateurs/:id/photo — a listed user's photo bytes (team token). */
  photo: (jeton, id) => api.get(`/auth/equipe/utilisateurs/${id}/photo`, avec(jeton, { responseType: "blob" })),
  /** POST /auth/equipe/selection — the PIN switch: the user's OWN JWT (no refresh token). */
  selection: (jeton, user_id, pin) => api.post("/auth/equipe/selection", { user_id, pin }, avec(jeton)),
  /** POST /auth/equipe/selection/prolonger — the picked user is active: a new JWT (never /auth/refresh). */
  prolonger: () => api.post("/auth/equipe/selection/prolonger"),
  /** POST /auth/equipe/changer — « Changer d'utilisateur »: the selection ends server-side. */
  changer: () => api.post("/auth/equipe/changer"),
  /** POST /auth/equipe/preuve — the owner picked by PIN re-types their own password (XD-296). */
  preuve: (mot_de_passe) => api.post("/auth/equipe/preuve", { mot_de_passe }),
};

export const compteEquipeApi = {
  /** GET /equipe/compte — the account (or null), the proposed identifier, the code recipients, the rules. */
  etat: () => api.get("/equipe/compte"),
  /** PUT /equipe/compte/mot-de-passe — create (201) or rotate (200); ends every live workstation on rotation. */
  definirMotDePasse: (mot_de_passe, mot_de_passe_proprietaire) =>
    api.put("/equipe/compte/mot-de-passe", { mot_de_passe, mot_de_passe_proprietaire }),
  /** PATCH /equipe/compte — (de)activate; the reactivation takes the owner's password. */
  changerEtat: (actif, mot_de_passe_proprietaire) =>
    api.patch("/equipe/compte", mot_de_passe_proprietaire === undefined ? { actif } : { actif, mot_de_passe_proprietaire }),
  /** GET /equipe/sessions — the workstations, paginated (etat ACTIVE | EXPIREE | REVOQUEE | TOUTES). */
  sessions: (params = {}) => api.get("/equipe/sessions", { params }),
  /** DELETE /equipe/sessions/:id — revoke one workstation. */
  revoquer: (id) => api.delete(`/equipe/sessions/${id}`),
  /** DELETE /equipe/sessions — revoke every live workstation. */
  revoquerToutes: () => api.delete("/equipe/sessions"),
};
